import { Component, ElementRef, Input, ViewChild, ViewContainerRef } from "@angular/core";
import { PonderTasksClient } from "@apiserver-rest/ponder-tasks-client";
import { PredefinedTask, RunTaskRequest, RunTaskResponse, SavePredefinedTaskRequest, TaskDescription, TaskResponse } from "@apiserver-rest/ponder-tasks-client-base";
import { EventBroker } from "@ponder-core/events/eventBroker";
import { ConfirmationModalComponent } from "@ponder-shared/modals/confirmation-modal.component";
import { prettifyJson } from "@ponder-shared/utils/prettifyJson";
import { take } from "rxjs";

@Component({
    selector: "task-run",
    imports: [],
    templateUrl: "./task-run.component.html"
})
export class TaskRunComponent {
    @Input({ required: true }) public task!: TaskDescription;

    @ViewChild("confirmationModalTemplate", { read: ViewContainerRef }) private confirmationModalVcr!: ViewContainerRef;
    @ViewChild("propertiesTextArea") private propertiesTextAreaRef!: ElementRef<HTMLTextAreaElement>;
    @ViewChild("timeoutSecondsInput") private timeoutSecondsInputRef!: ElementRef<HTMLInputElement>;
    @ViewChild("predefinedSaveAsNameInput") private predefinedSaveAsNameInputRef!: ElementRef<HTMLInputElement>;

    public taskParametersJson!: string;
    public predefinedTasks?: PredefinedTask[];
    public timeoutSeconds: number = 1800;

    public constructor(private ponderTasksClient: PonderTasksClient, private events: EventBroker) {

    }

    public ngAfterViewInit(): void {
        this.refreshParametersJson();
    }

    public ngOnChanges(): void {
        this.refreshParametersJson();
    }
    
    private refreshParametersJson(): void {
        this.timeoutSeconds = 1800;
        this.predefinedTasks = this.task.predefined;
        this.taskParametersJson = prettifyJson(this.task.parametersJson);

        if (this.propertiesTextAreaRef) {
            this.propertiesTextAreaRef.nativeElement.value = this.taskParametersJson;
        }

        if (this.predefinedSaveAsNameInputRef) {
            this.predefinedSaveAsNameInputRef.nativeElement.value = "";
        }
    }

    public onPredefinedSelected(predefinedName: string | undefined): void {
        if (!this.propertiesTextAreaRef) {
            return;
        }

        var predefinedTask = this.predefinedTasks?.find(t => t.definitionName == predefinedName);
        
        // ... todo alert if modified so dont accidentally erase stuff ...

        if (predefinedTask) {
            this.timeoutSeconds = predefinedTask.timeoutSeconds;
            this.propertiesTextAreaRef.nativeElement.value = prettifyJson(predefinedTask.taskSpecs);

            if (this.predefinedSaveAsNameInputRef) {
                this.predefinedSaveAsNameInputRef.nativeElement.value = predefinedTask.definitionName;
            }
        } else {
            this.timeoutSeconds = 1800;
            this.propertiesTextAreaRef.nativeElement.value = this.taskParametersJson

            if (this.predefinedSaveAsNameInputRef) {
                this.predefinedSaveAsNameInputRef.nativeElement.value = "";
            }
        }
    }

    // -------------------------------------------------------------------------------------
    // Save Predefined
    // -------------------------------------------------------------------------------------

    public isSavingPredefined: boolean = false;
    public savingPredefinedError: boolean = false;

    public onSaveClicked(): void {
        if (!this.predefinedSaveAsNameInputRef || this.isSavingPredefined) {
            return;
        }

        var definitionName = this.predefinedSaveAsNameInputRef.nativeElement.value;

        if (definitionName.length < 3) {
            this.savingPredefinedError = true;
            return;
        }

        var existingPredefined = this.predefinedTasks?.find(t => t.definitionName.toLowerCase() == definitionName.toLowerCase());

        if (existingPredefined) {
            // Name already in use. Need to confirm overwrite.
            var confirmationModalRef = this.confirmationModalVcr.createComponent(ConfirmationModalComponent);

            confirmationModalRef.instance.title = "Overwrite Predefined";
            confirmationModalRef.instance.body = `There already exists a predefined task with the name '${definitionName}'. Overwrite it?`;
            confirmationModalRef.instance.centered = false;
            confirmationModalRef.instance.onAcceptCallback = () => { this.savePredefined(definitionName); }
        } else {
            this.savePredefined(definitionName);
        }
    }

    private savePredefined(definitionName: string): void {
        if (this.isSavingPredefined) {
            return;
        }

        if (!this.propertiesTextAreaRef || !this.timeoutSecondsInputRef) {
            return;
        }

        this.isSavingPredefined = true;
        this.savingPredefinedError = false;

        this.ponderTasksClient
            .tasksPredefinedPost(new SavePredefinedTaskRequest({
                taskName: this.task.taskName,
                definitionName: definitionName,
                taskParametersJson: this.propertiesTextAreaRef.nativeElement.value,
                timeoutSeconds: Number.parseInt(this.timeoutSecondsInputRef.nativeElement.value)
            }))
            .pipe(take(1))
            .subscribe({
                next: (response: TaskResponse) => {
                    this.savingPredefinedError = !response.success;
                },
                error: () => {
                    this.savingPredefinedError = true;
                }
            })
            .add(() => { this.isSavingPredefined = false; });
    }

    // -------------------------------------------------------------------------------------
    // Run Task
    // -------------------------------------------------------------------------------------

    public isRunningTask: boolean = false;
    public runningTaskError: boolean = false;

    public onRunClicked(): void {
        if (this.isRunningTask) {
            return;
        }

        this.isRunningTask = true;
        this.runningTaskError = false;

        this.ponderTasksClient
            .tasksRunPost(new RunTaskRequest({
                taskName: this.task.taskName,
                timeoutSeconds: Number.parseInt(this.timeoutSecondsInputRef.nativeElement.value),
                parametersJson: this.propertiesTextAreaRef.nativeElement.value
            }))
            .pipe(take(1))
            .subscribe({
                next: (response: RunTaskResponse) => {
                    if (response.success && response.taskGuid) {
                        this.events.admin.viewTaskEvent.emit(response.taskGuid);
                    } else {
                        this.runningTaskError = true;
                    }
                },
                error: () => {
                    this.runningTaskError = true;
                }
            })
            .add(() => { this.isRunningTask = false; })
    }
}
